/**
 * Server state for orders, customers and payments.
 *
 * The settings pattern: reads are `queryOptions`, writes are mutations that invalidate what
 * their change could have altered. A payment changes the order's money; a status change can
 * change what money it takes; both change the list.
 */

import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, unwrapEmpty } from "@/api/problem";
import type { components } from "@/api/schema";
import type { OrderStatus } from "./lifecycle";

export type Order = components["schemas"]["OrderOut"];
export type OrderSummary = components["schemas"]["OrderSummaryOut"];
export type Customer = components["schemas"]["CustomerOut"];
export type Payment = components["schemas"]["PaymentOut"];
export type PaymentMethod = components["schemas"]["MethodOut"];
export type OrderMoney = components["schemas"]["OrderMoneyOut"];
export type OrderPurchase = components["schemas"]["OrderCostOut"];
export type OrderBody = components["schemas"]["OrderIn"];
export type PaymentBody = components["schemas"]["PaymentIn"];
export type RefundBody = components["schemas"]["RefundIn"];

// --- reads --------------------------------------------------------------------------------------

/** Orders newest first, a page at a time, optionally of one status. */
export function ordersQuery(status: OrderStatus | null) {
  return infiniteQueryOptions({
    queryKey: ["orders", status ?? "all"],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/orders", {
          params: {
            query: { ...(status ? { status } : {}), ...(pageParam ? { cursor: pageParam } : {}) },
          },
        }),
      ),
    initialPageParam: "",
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
}

/** One business's orders, newest first, for attaching a receipt line to them. */
export function businessOrdersQuery(businessId: string) {
  return infiniteQueryOptions({
    queryKey: ["orders", "business", businessId],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/orders", {
          params: {
            query: {
              business_id: businessId,
              limit: 100,
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: "",
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
}

export function orderQuery(orderId: string) {
  return queryOptions({
    queryKey: ["order", orderId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/orders/{order_id}", { params: { path: { order_id: orderId } } }),
      ),
  });
}

export function orderMoneyQuery(orderId: string) {
  return queryOptions({
    queryKey: ["order", orderId, "money"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/orders/{order_id}/money", {
          params: { path: { order_id: orderId } },
        }),
      ),
  });
}

export function orderPaymentsQuery(orderId: string) {
  return queryOptions({
    queryKey: ["order", orderId, "payments"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/orders/{order_id}/payments", {
          params: { path: { order_id: orderId } },
        }),
      ),
  });
}

export function orderPurchasesQuery(orderId: string) {
  return queryOptions({
    queryKey: ["order", orderId, "purchases"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/orders/{order_id}/allocations", {
          params: { path: { order_id: orderId } },
        }),
      ),
  });
}

export const customersQuery = queryOptions({
  queryKey: ["customers"],
  queryFn: async () => unwrap(await api.GET("/api/v1/customers")),
});

export const paymentMethodsQuery = queryOptions({
  queryKey: ["payment-methods"],
  queryFn: async () => unwrap(await api.GET("/api/v1/payment-methods")),
});

// --- writes -------------------------------------------------------------------------------------

async function invalidate(queryClient: QueryClient, ...keys: unknown[][]) {
  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CustomerIn"]) =>
      unwrap(await api.POST("/api/v1/customers", { body })),
    onSuccess: () => invalidate(queryClient, ["customers"]),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: { id: string } & Partial<components["schemas"]["CustomerPatch"]>) =>
      unwrap(
        await api.PATCH("/api/v1/customers/{customer_id}", {
          params: { path: { customer_id: id } },
          body: body as components["schemas"]["CustomerPatch"],
        }),
      ),
    // An order shows its customer's name.
    onSuccess: () => invalidate(queryClient, ["customers"], ["orders"], ["order"]),
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderBody) => unwrap(await api.POST("/api/v1/orders", { body })),
    onSuccess: async (order) => {
      queryClient.setQueryData(orderQuery(order.id).queryKey, order);
      await invalidate(queryClient, ["orders"]);
    },
  });
}

export function useUpdateOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["OrderPatch"]) =>
      unwrap(
        await api.PATCH("/api/v1/orders/{order_id}", {
          params: { path: { order_id: orderId } },
          body,
        }),
      ),
    onSuccess: async (order) => {
      queryClient.setQueryData(orderQuery(orderId).queryKey, order);
      // The quote is what the balance is measured against.
      await invalidate(queryClient, ["orders"], ["order", orderId, "money"]);
    },
  });
}

export function useChangeStatus(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (to: OrderStatus) =>
      unwrap(
        await api.POST("/api/v1/orders/{order_id}/status", {
          params: { path: { order_id: orderId } },
          body: { to },
        }),
      ),
    onSuccess: async (order) => {
      queryClient.setQueryData(orderQuery(orderId).queryKey, order);
      // A cancelled order owes nothing, so its balance changes too.
      await invalidate(queryClient, ["orders"], ["order", orderId, "money"]);
    },
  });
}

function afterMoney(queryClient: QueryClient, orderId: string) {
  return invalidate(queryClient, ["order", orderId], ["orders"]);
}

export function useRecordPayment(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: PaymentBody) =>
      unwrap(
        await api.POST("/api/v1/orders/{order_id}/payments", {
          params: { path: { order_id: orderId } },
          body,
        }),
      ),
    onSuccess: () => afterMoney(queryClient, orderId),
  });
}

export function useRefund(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RefundBody) =>
      unwrap(
        await api.POST("/api/v1/orders/{order_id}/refund", {
          params: { path: { order_id: orderId } },
          body,
        }),
      ),
    onSuccess: () => afterMoney(queryClient, orderId),
  });
}

export function useDeletePayment(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentId: string) =>
      unwrapEmpty(
        await api.DELETE("/api/v1/payments/{payment_id}", {
          params: { path: { payment_id: paymentId } },
        }),
      ),
    onSuccess: () => afterMoney(queryClient, orderId),
  });
}
